import * as THREE from "three"
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js"

/**
 * Single source of truth for the location 3D scene editor's light palette.
 * Every value here is what measureReference() checks against — do not retune without
 * an explicit request. Visual reference: variants.html palette "Светлый" (three 0.170).
 */
export const PALETTE = {
  zenith: "#c4c7cb",
  horizon: "#e2e2e1",
  ground: "#d0d1d2",
  mark: "#aeb1b5",
  markMaj: "#979a9f",
  axisX: "#c4404f",
  axisY: "#7fae2e",
  tile: "#b9b5ab",
  seam: "#a39f95",
  wall: "#aaa69c",
} as const

const TILES_PER_SIDE = 40

export function hexToNumber(hex: string): number {
  return parseInt(hex.replace("#", ""), 16)
}

export type SkyFamily = {
  horizon: string
  zenith: string
  ground: string
  mark: string
  markMaj: string
}

function parseHex(hex: string): [number, number, number] {
  const h = hex.replace("#", "").toLowerCase()
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

function toHexColor(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)))
  return `#${[clamp(r), clamp(g), clamp(b)].map((n) => n.toString(16).padStart(2, "0")).join("")}`
}

/** Scale a canonical token relative to a custom horizon, preserving channel ratios. */
function relativeToHorizon(horizonHex: string, referenceToken: string): string {
  const [hr, hg, hb] = parseHex(PALETTE.horizon)
  const [tr, tg, tb] = parseHex(horizonHex)
  const [rr, rg, rb] = parseHex(referenceToken)
  const scale = (channel: number, refH: number, refT: number) => (refH <= 1e-6 ? channel : channel * (refT / refH))
  return toHexColor(scale(tr, hr, rr), scale(tg, hg, rg), scale(tb, hb, rb))
}

/**
 * Sky family for a given horizon. Default horizon returns the exact canonical tokens
 * (what measureReference checks); a custom horizon keeps the same channel ratios.
 */
export function deriveSkyFamily(horizonHex: string): SkyFamily {
  const horizon = horizonHex.toLowerCase()
  if (horizon === PALETTE.horizon) {
    return {
      horizon: PALETTE.horizon,
      zenith: PALETTE.zenith,
      ground: PALETTE.ground,
      mark: PALETTE.mark,
      markMaj: PALETTE.markMaj,
    }
  }
  return {
    horizon,
    zenith: relativeToHorizon(horizon, PALETTE.zenith),
    ground: relativeToHorizon(horizon, PALETTE.ground),
    mark: relativeToHorizon(horizon, PALETTE.mark),
    markMaj: relativeToHorizon(horizon, PALETTE.markMaj),
  }
}

/**
 * Render/colorspace settings that affect every color in the scene.
 * NeutralToneMapping + sRGB output is required for on-screen hex to match PALETTE.
 */
export function applyLightPaletteRenderSettings(scene: THREE.Scene, renderer: THREE.WebGLRenderer): THREE.Texture {
  THREE.ColorManagement.enabled = true
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.toneMapping = THREE.NeutralToneMapping
  renderer.toneMappingExposure = 1
  scene.fog = null
  scene.background = null
  const pmrem = new THREE.PMREMGenerator(renderer)
  const room = new RoomEnvironment()
  const envTexture = pmrem.fromScene(room, 0.04).texture
  room.dispose()
  pmrem.dispose()
  scene.environment = envTexture
  scene.environmentIntensity = 0.35
  scene.backgroundIntensity = 1
  return envTexture
}

export type LightPaletteRig = {
  group: THREE.Group
  hemi: THREE.HemisphereLight
  key: THREE.DirectionalLight
  fill: THREE.DirectionalLight
}

/** Base rig intensities at factor 1 — reference values for scaleLightPaletteRig. */
export const AMBIENT_RIG_BASE = { hemi: 0.7, key: 1.6, fill: 0.55 } as const

/** Exactly three lights — white, no shadows. Caller keeps any other lights it needs (gizmo glint, user lamp). */
export function createLightPaletteRig(): LightPaletteRig {
  const group = new THREE.Group()
  group.name = "lightPaletteRig"
  const hemi = new THREE.HemisphereLight("#ffffff", PALETTE.ground, AMBIENT_RIG_BASE.hemi)
  const key = new THREE.DirectionalLight("#ffffff", AMBIENT_RIG_BASE.key)
  key.position.set(-7, 20, 5)
  const fill = new THREE.DirectionalLight("#ffffff", AMBIENT_RIG_BASE.fill)
  fill.position.set(10, 4, 6)
  group.add(hemi, key, fill)
  return { group, hemi, key, fill }
}

/** Scale the rig off its base intensities — e.g. to follow the user's lamp-intensity slider. */
export function scaleLightPaletteRig(rig: LightPaletteRig, factor: number) {
  rig.hemi.intensity = AMBIENT_RIG_BASE.hemi * factor
  rig.key.intensity = AMBIENT_RIG_BASE.key * factor
  rig.fill.intensity = AMBIENT_RIG_BASE.fill * factor
}

/** Port of variants.html paintTiles — 40x40 on 2048, HSL jitter, 4px grout, deterministic seed. */
export function createPaletteTileTexture(renderer: THREE.WebGLRenderer): THREE.CanvasTexture {
  const S = 2048
  const N = TILES_PER_SIDE
  const s = S / N
  const canvas = document.createElement("canvas")
  canvas.width = S
  canvas.height = S
  const ctx = canvas.getContext("2d")
  const base = new THREE.Color(PALETTE.tile)
  let seed = 11
  const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647 - 0.5
  if (ctx) {
    for (let i = 0; i < N; i += 1) {
      for (let j = 0; j < N; j += 1) {
        ctx.fillStyle = `#${base
          .clone()
          .offsetHSL(0, rnd() * 0.02, rnd() * 0.025)
          .getHexString()}`
        ctx.fillRect(i * s, j * s, s, s)
      }
    }
    ctx.fillStyle = PALETTE.seam
    for (let i = 0; i <= N; i += 1) {
      ctx.fillRect(i * s - 2, 0, 4, S)
      ctx.fillRect(0, i * s - 2, S, 4)
    }
  }
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = renderer.capabilities.getMaxAnisotropy()
  texture.needsUpdate = true
  return texture
}

export type HeightFadeUniforms = {
  uFadeColor: { value: THREE.Color }
  uFadeY: { value: THREE.Vector2 }
}

/**
 * Dissolve a material toward `hex` as world-space Y drops from fadeFrom to fadeTo.
 * Used instead of scene.fog (which reads wrong at this scene's scale — see docs).
 * Returns the live uniforms so the caller can retarget fadeFrom/fadeTo on geometry rebuilds
 * without recompiling the shader.
 */
export function applyHeightFade(
  material: THREE.MeshStandardMaterial,
  hex: string,
  fadeFrom: number,
  fadeTo: number,
): HeightFadeUniforms {
  const uniforms: HeightFadeUniforms = {
    uFadeColor: { value: new THREE.Color(hex).convertLinearToSRGB() },
    uFadeY: { value: new THREE.Vector2(fadeFrom, fadeTo) },
  }
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uFadeColor = uniforms.uFadeColor
    shader.uniforms.uFadeY = uniforms.uFadeY
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nvarying float vWorldY;")
      .replace(
        "#include <project_vertex>",
        "#include <project_vertex>\nvWorldY = (modelMatrix * vec4(transformed, 1.0)).y;",
      )
    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        "#include <common>\nvarying float vWorldY;\nuniform vec3 uFadeColor;\nuniform vec2 uFadeY;",
      )
      .replace(
        "#include <dithering_fragment>",
        "gl_FragColor.rgb = mix(gl_FragColor.rgb, uFadeColor, smoothstep(uFadeY.x, uFadeY.y, vWorldY));\n#include <dithering_fragment>",
      )
  }
  material.needsUpdate = true
  return uniforms
}

export type SceneColorDiagnostics = {
  three: string
  colorManagement: boolean
  outputColorSpace: string
  toneMapping: number
  toneMappingExposure: number
  pixelRatio: number
  fog: string | null
  environment: boolean
  environmentIntensity: number
  background: string | null
  lights: { name: string; type: string; color: string; intensity: number }[]
  materials: {
    mesh: string
    type: string
    color: string | null
    map: boolean
    mapColorSpace: string | null
    roughness: number | undefined
    metalness: number | undefined
    toneMapped: boolean | undefined
  }[]
}

/** Prints every setting that affects on-screen color. Reach for this before retuning hex. */
export function diagnose(scene: THREE.Scene, renderer: THREE.WebGLRenderer): SceneColorDiagnostics {
  const report: SceneColorDiagnostics = {
    three: THREE.REVISION,
    colorManagement: THREE.ColorManagement.enabled,
    outputColorSpace: renderer.outputColorSpace,
    toneMapping: renderer.toneMapping,
    toneMappingExposure: renderer.toneMappingExposure,
    pixelRatio: renderer.getPixelRatio(),
    fog: scene.fog ? scene.fog.name || "fog" : null,
    environment: Boolean(scene.environment),
    environmentIntensity: scene.environmentIntensity,
    background:
      scene.background && (scene.background as THREE.Color).isColor
        ? `#${(scene.background as THREE.Color).getHexString()}`
        : scene.background
          ? "texture"
          : null,
    lights: [],
    materials: [],
  }
  scene.traverse((object) => {
    const light = object as THREE.Light
    if (light.isLight) {
      report.lights.push({
        name: light.name,
        type: light.type,
        color: `#${light.color.getHexString()}`,
        intensity: light.intensity,
      })
    }
    const mesh = object as THREE.Mesh
    if (mesh.isMesh) {
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      for (const m of materials) {
        const standard = m as THREE.MeshStandardMaterial
        report.materials.push({
          mesh: mesh.name,
          type: m.type,
          color: standard.color ? `#${standard.color.getHexString(THREE.SRGBColorSpace)}` : null,
          map: Boolean(standard.map),
          mapColorSpace: standard.map ? standard.map.colorSpace : null,
          roughness: standard.roughness,
          metalness: standard.metalness,
          toneMapped: m.toneMapped,
        })
      }
    }
  })
  console.log("[light-palette] diagnose", JSON.stringify(report, null, 2))
  return report
}

/** Reference pixel colors for the default palette at the canonical measurement angle. */
export const REFERENCE = {
  sky: "#dcdcdc",
  roof: "#c7c3b8",
  leftWall: "#9d998f",
  rightWall: "#7f7c73",
} as const

export type ReferenceCheck = {
  ok: boolean
  report: Record<string, { expected: string; got: string; diff: number; pass: boolean }>
  /** What each sample point's camera ray actually hit — use this to catch a geometry/camera mismatch. */
  hits: Record<string, { mesh: string | null; materialColor: string | null; map: boolean; distance: number | null }>
}

function readPixelAverage(
  gl: WebGL2RenderingContext | WebGLRenderingContext,
  sx: number,
  sy: number,
): [number, number, number] {
  const w = gl.drawingBufferWidth
  const h = gl.drawingBufferHeight
  const r = 3
  const x = Math.round(sx * w) - r
  const y = Math.round((1 - sy) * h) - r
  const span = 2 * r + 1
  const buf = new Uint8Array(span * span * 4)
  gl.readPixels(x, y, span, span, gl.RGBA, gl.UNSIGNED_BYTE, buf)
  const sum: [number, number, number] = [0, 0, 0]
  for (let i = 0; i < buf.length; i += 4) {
    sum[0] += buf[i] ?? 0
    sum[1] += buf[i + 1] ?? 0
    sum[2] += buf[i + 2] ?? 0
  }
  const n = buf.length / 4
  return [Math.round(sum[0] / n), Math.round(sum[1] / n), Math.round(sum[2] / n)]
}

function toHex(rgb: [number, number, number]): string {
  return `#${rgb.map((v) => v.toString(16).padStart(2, "0")).join("")}`
}

/**
 * Renders the canonical measurement angle relative to `platformRoot` and compares pixels
 * against REFERENCE. Does not touch the main frame (renders to the same canvas but the
 * caller's next loop tick repaints over it).
 */
export function measureReference(
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
  platformRoot: THREE.Object3D,
  tolerance = 8,
): ReferenceCheck {
  const box = new THREE.Box3().setFromObject(platformRoot)
  const size = box.getSize(new THREE.Vector3())
  const w = Math.max(size.x, size.z)
  const center = box.getCenter(new THREE.Vector3())
  const top = new THREE.Vector3(center.x, box.max.y, center.z)

  const canvas = renderer.domElement
  const camera = new THREE.PerspectiveCamera(40, canvas.width / canvas.height, w * 0.01, w * 200)
  camera.position.copy(top).add(new THREE.Vector3(1.464, 1.22, 2.051).multiplyScalar(w))
  camera.lookAt(top)
  camera.updateMatrixWorld()

  const points: Record<string, THREE.Vector3> = {
    roof: top.clone(),
    leftWall: new THREE.Vector3(center.x, box.max.y - 0.4 * w, box.max.z),
    rightWall: new THREE.Vector3(box.max.x, box.max.y - 0.4 * w, center.z),
  }

  const gl = renderer.getContext()
  renderer.render(scene, camera)

  const got: Record<string, [number, number, number]> = { sky: readPixelAverage(gl, 0.5, 0.03) }
  for (const [key, point] of Object.entries(points)) {
    const projected = point.clone().project(camera)
    got[key] = readPixelAverage(gl, (projected.x + 1) / 2, (1 - projected.y) / 2)
  }

  // Cross-check: does the camera ray toward each point actually hit the surface we think it does?
  const raycaster = new THREE.Raycaster()
  const hits: ReferenceCheck["hits"] = {}
  for (const [key, point] of Object.entries(points)) {
    const direction = point.clone().sub(camera.position).normalize()
    raycaster.set(camera.position, direction)
    const [hit] = raycaster.intersectObject(platformRoot, true)
    if (!hit || !(hit.object as THREE.Mesh).isMesh) {
      hits[key] = { mesh: null, materialColor: null, map: false, distance: null }
      continue
    }
    const mesh = hit.object as THREE.Mesh
    const material = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as
      | THREE.MeshStandardMaterial
      | undefined
    hits[key] = {
      mesh: mesh.name || mesh.userData?.kind || mesh.type,
      materialColor: material?.color ? `#${material.color.getHexString(THREE.SRGBColorSpace)}` : null,
      map: Boolean(material?.map),
      distance: hit.distance,
    }
  }

  const report: ReferenceCheck["report"] = {}
  let ok = true
  for (const [key, expected] of Object.entries(REFERENCE)) {
    const expectedColor = new THREE.Color(expected)
    const expectedHex = expectedColor.getHexString(THREE.SRGBColorSpace)
    const expectedRgb = (expectedHex.match(/../g) ?? []).map((h) => parseInt(h, 16))
    const gotRgb = got[key] ?? [0, 0, 0]
    const diff = Math.max(...gotRgb.map((v, i) => Math.abs(v - (expectedRgb[i] ?? 0))))
    report[key] = { expected, got: toHex(gotRgb), diff, pass: diff <= tolerance }
    ok = ok && diff <= tolerance
  }
  console.log("[light-palette] reference check", ok ? "PASS" : "FAIL", JSON.stringify(report, null, 2))
  console.log("[light-palette] sample ray hits", JSON.stringify(hits, null, 2))
  return { ok, report, hits }
}
